// src/components/ui/CustomTooltip.tsx
import React, { useState, useMemo } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { Typography, Stack, useMediaQuery, Box, ClickAwayListener } from '@mui/material';
import DetailItem from './DetailItem';
import { tokens } from '../../../theme';

export interface TooltipSection {
  title: string;
  items: Array<{ label: string; value: React.ReactNode }>;
}

interface CustomTooltipProps extends Omit<TooltipProps, 'title' | 'content'> {
  /** Structured data rendered as titled label/value sections. */
  sections?: TooltipSection[];
  /** Plain content for simple tooltips. */
  content?: React.ReactNode;
  /** Section title color. */
  accentColor?: string;
  /** Wrap the trigger in a full-width block instead of inline-block. */
  fullWidth?: boolean;
  /**
   * Touch screens: render the trigger without a tooltip. For small details inside a card that is
   * itself tappable, where a near-miss would open the card instead (the detail view shows it all).
   */
  disableOnTouch?: boolean;
}

const TooltipRoot = styled(
  React.forwardRef(({ className, isMobile, ...props }: TooltipProps & { isMobile: boolean }, ref) => (
    <Tooltip {...props} classes={{ popper: className }} ref={ref} />
  ))
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'rgba(20, 20, 22, 0.86)',
    backdropFilter: 'blur(20px) saturate(150%)',
    WebkitBackdropFilter: 'blur(20px) saturate(150%)',
    color: theme.palette.text.primary,
    minWidth: 220,
    maxWidth: 320,
    fontSize: theme.typography.pxToRem(13),
    border: `1px solid ${tokens.borderStrong}`,
    borderRadius: 16,
    boxShadow: `${tokens.glass.highlight}, 0 24px 56px -16px rgba(0, 0, 0, 0.9)`,
    padding: theme.spacing(1.75, 2),
  },
}));

/**
 * Data tooltip: structured sections on desktop (hover), tap-to-open on phones.
 */
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  sections,
  content,
  children,
  accentColor = 'secondary.main',
  fullWidth = false,
  disableOnTouch = false,
  ...tooltipProps
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  // Touch screens (phones, iPad) have no hover: open on tap instead.
  const isTouch = useMediaQuery('(hover: none)');
  const isMobile = isPhone || isTouch;

  const titleContent: React.ReactNode | null = useMemo(() => {
    if (sections && sections.length > 0) {
      return (
        <Stack spacing={1.25}>
          {sections.map((section, index) => (
            <Stack key={`${section.title}-${index}`} spacing={0.25}>
              {section.title && (
                <Typography
                  variant="overline"
                  sx={{ color: accentColor, lineHeight: 1.6, display: 'block' }}
                >
                  {section.title}
                </Typography>
              )}
              {section.items.map((item, itemIndex) =>
                item.label ? (
                  <DetailItem key={`${item.label}-${itemIndex}`} label={item.label} value={item.value} dense />
                ) : (
                  <Typography key={itemIndex} sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                    {item.value}
                  </Typography>
                )
              )}
            </Stack>
          ))}
        </Stack>
      );
    }
    if (content) {
      return <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{content}</Typography>;
    }
    return null;
  }, [sections, content, accentColor]);

  if (!titleContent || (disableOnTouch && isTouch)) return <>{children}</>;

  return isMobile ? (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <TooltipRoot
        title={titleContent}
        open={open}
        onClose={() => setOpen(false)}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        placement="top"
        PopperProps={{
          modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            {
              name: 'preventOverflow',
              options: { altAxis: true, tether: false, rootBoundary: 'viewport', padding: 12 },
            },
          ],
        }}
        isMobile={isMobile}
        {...tooltipProps}
      >
        <Box
          data-tooltip-trigger
          onClick={() => setOpen((v) => !v)}
          sx={fullWidth ? { display: 'block', width: '100%', height: '100%' } : { display: 'inline-block' }}
        >
          {children}
        </Box>
      </TooltipRoot>
    </ClickAwayListener>
  ) : (
    <TooltipRoot title={titleContent} isMobile={isMobile} {...tooltipProps}>
      <Box data-tooltip-trigger sx={fullWidth ? { display: 'block', width: '100%', height: '100%' } : { display: 'inline-block' }}>
        {children}
      </Box>
    </TooltipRoot>
  );
};

export default CustomTooltip;
